import {Injectable} from '@angular/core';
import {IExceptionDatesModal, IExceptionTable, IHourTable, ITimetable} from '../../models/timetable';
import {isAfter, isEqual, setHours} from 'date-fns';

@Injectable({
  providedIn: 'root'
})
export class TimetableService {

  constructor() { }

  /**
   * check for duplicate values in a list of items
   * @param list lista
   */
  checkDuplicate(list: (string | number | bigint | boolean  | undefined | symbol)[]): boolean {
    return new Set(list).size !== list.length;
  }

  /**
   * Check if there are duplicate days
   * @param timeTable timeTable
   * @param throwError determina si se dispara o no el error
   */
  checkDuplicateDays(timeTable: ITimetable[], throwError = false): boolean {
    const checkDuplicateDays = this.checkDuplicate(timeTable.map((timeTbl: Partial<ITimetable>) => timeTbl.day));
    if (checkDuplicateDays && throwError) { throw new Error('There are duplicate days in the group\'s schedules'); }
    return checkDuplicateDays;
  }

  /**
   * Check that the days are valid
   * @param timeTable objetio time table
   * @param throwError determina si se dispara o no el error
   */
  checkValidDays(timeTable: Partial<ITimetable>[], throwError = false): boolean {
    const days = timeTable.map((timeTbl: Partial<ITimetable>) => timeTbl.day);
    const invalidDay = days.some((day: number | void) => day < 0 || day > 6);
    if (invalidDay && throwError) { throw new Error('Days can only be between the range 0 and 6'); }

    return invalidDay;
  }

  /**
   * Check if the time ranges are valid
   * @param timeTables[] timeTables arreglo de timetables
   * @param throwError determina si se dispara o no el error
   */
  checkTimeRanges(timeTables: ITimetable[] | IExceptionTable[], throwError = false): boolean {
    const currentDate = new Date();
    let validTimeRanges = 0;
    for (const timeTable of timeTables) {
      for (const hourTable of (timeTable.hourTable ?? [])) {
        const startDate = setHours(currentDate, hourTable.startHour ?? 0).setMinutes(hourTable.startMinutes ?? 0);
        const endDate = setHours(currentDate, hourTable.endHour ?? 0).setMinutes(hourTable.endMinutes ?? 0);
        if (isAfter(startDate, endDate) || isEqual(startDate, endDate)) {
          hourTable.hasError = 'La hora de fin debe ser posterior a la hora de inicio';
          validTimeRanges ++;
          // break;
        } else {
          delete hourTable.hasError;
        }
      }
    }
    if (validTimeRanges > 0 && throwError) { throw new Error('Invalid date ranges, ' + validTimeRanges); }
    return validTimeRanges > 0;
  }

  /**
   * Check for schedule splicing
   */
  checkTimeSplicing(timeTables: ITimetable[] | IExceptionTable[], throwError = false): boolean {
    const currentDate = new Date();
    let timeSplicing = 0;
    for (const timeTable of timeTables) {
      timeTable.hourTable = timeTable.hourTable?.sort((current: Partial<IHourTable>, next: Partial<IHourTable>) => {
        const endDate = setHours(currentDate, current.startHour ?? 0).setMinutes(current.startMinutes ?? 0);
        const startDate = setHours(currentDate, next.startHour ?? 0).setMinutes(next.startMinutes ?? 0);
        return isAfter(startDate, endDate) ? -1 : 1;
      }) ?? [];

      for (const [index, value] of timeTable.hourTable.entries()) {
        if (timeTable.hourTable[index + 1]) {
          const currentEndDate = setHours(currentDate, value.endHour ?? 0).setMinutes(value.endMinutes ?? 0);
          const nextStartDate = setHours(currentDate, timeTable.hourTable[index + 1].startHour ?? 0)
            .setMinutes(timeTable.hourTable[index + 1].startMinutes ?? 0);
          if (isAfter(currentEndDate, nextStartDate) || isEqual(currentDate, nextStartDate)) {
            timeTable.hourTable[index + 1].hasError = 'La hora entra en conflicto con otro horario';
            timeSplicing ++;
            break;
          } else {
            delete timeTable.hourTable[index + 1].hasError;
          }
        }
      }
    }
    if (timeSplicing > 0 && throwError) { throw new Error('Las fechas se empalman'); }
    return timeSplicing > 0;
  }

  /**
   * Check if the time ranges are valid (exceptions)
   * @param exceptions arreglo de excepciones
   * @param throwError determina si se dispara o no el error
   */
  checkExceptionTimeRange(exceptions: IExceptionTable[], throwError = false): boolean {
    let validExceptionTimeRanges = true;
    for (const exception of exceptions) {
      // tslint:disable-next-line:no-non-null-assertion
      const startDate = new Date(exception.start!.year!, exception.start!.month!, exception.start!.day!);
      // tslint:disable-next-line:no-non-null-assertion
      const endDate = new Date(exception.end!.year!, exception.end!.month!, exception.end!.day!);
      if (isAfter(startDate, endDate) || isEqual(startDate, endDate)) {
        validExceptionTimeRanges = false;
        break;
      }
    }
    if (!validExceptionTimeRanges && throwError) { throw new Error('[Exception] Start date must be less than end date'); }
    return validExceptionTimeRanges;
  }

  /**
   * Checa que la fecha de inicio sea menor que la fecha de fin
   * @param dateException arreglo de excepciones
   * @param throwError determina si se dispara o no el error
   */
  checkExceptionDateTimeRange(dateException: IExceptionDatesModal , throwError = false): boolean {
    let validExceptionTimeRanges = true;
    const [firstDateException, secondDateException] = dateException.dates;
    if (isAfter(firstDateException, secondDateException)) {
      validExceptionTimeRanges = false;
      dateException.dateRangeError = 'La fecha de fin debe ser posterior a la fecha de inicio';
    } else {
      delete dateException.dateRangeError;
    }
    if (!validExceptionTimeRanges && throwError) { throw new Error('[Exception] Start date must be less than end date'); }
    return validExceptionTimeRanges;
  }

  checkExceptionTimeRanges(dateException: IExceptionDatesModal, throwError = false): boolean {
    let validTimeRanges = 0;
    for (const hour of (dateException.hours ?? [])) {
      if (isAfter(hour.start, hour.end) || isEqual(hour.start, hour.end)) {
        hour.hasError = 'La hora de fin debe ser posterior a la hora de inicio';
        validTimeRanges ++;
        // break;
      } else {
        delete hour.hasError;
      }
    }
    if (validTimeRanges > 0 && throwError) { throw new Error('Invalid exception time ranges, ' + validTimeRanges); }
    return validTimeRanges > 0;
  }

  checkExceptionTimeSplicing(dateException: IExceptionDatesModal, throwError = false): boolean {
    const currentDate = new Date();
    let timeSplicing = 0;
    // @ts-ignore
    dateException.hours = dateException.hours?.sort((current: Date, next: Date) => {
      return isAfter(current, next) ? -1 : 1;
    }) ?? [];

    for (let i = 0; i < dateException.hours.length; i++) {
      if (dateException.hours[i + 1]) {
        const currentEndDate = dateException.hours[i].end;
        const nextStartDate = dateException.hours[i + 1].start;
        if (isAfter(currentEndDate, nextStartDate) || isEqual(currentEndDate, nextStartDate) ) {
          dateException.hours[i + 1].hasError = 'La hora entra en conflicto con otro horario';
          timeSplicing ++;
          break;
        } else {
          delete dateException.hours[i + 1].hasError;
        }
      }
    }
    if (timeSplicing > 0 && throwError) { throw new Error('Las fechas se empalman'); }
    return timeSplicing > 0;
  }
}
